import { Link } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';

export function TermsOfServicePage() {
    return (
        <div className="bg-background-light dark:bg-background-dark text-slate-900 dark:text-slate-100 font-sans transition-colors duration-300 min-h-screen">
            <Navbar variant="landing" />

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Header */}
                <div className="mb-12 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4 font-display">
                        Terms of Service
                    </h1>
                    <p className="text-slate-600 dark:text-slate-400 text-lg">
                        Last Updated: January 10, 2026
                    </p>
                </div>

                {/* Content */}
                <div className="prose prose-slate dark:prose-invert max-w-none">
                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">1. Acceptance of Terms</h2>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            Welcome to UniScout ("we," "our," or "us"). By accessing or using our platform at uniscout.ai
                            (the "Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree
                            to these Terms, please do not use our Services.
                        </p>
                        <p className="text-slate-700 dark:text-slate-300">
                            These Terms constitute a legally binding agreement between you and UniScout. We reserve the right
                            to modify these Terms at any time. Your continued use of the Services after changes are posted
                            constitutes your acceptance of the modified Terms.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">2. Eligibility</h2>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            You must be at least 18 years old to use our Services. By using our Services, you represent and
                            warrant that you:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2">
                            <li>Are at least 18 years of age</li>
                            <li>Have the legal capacity to enter into these Terms</li>
                            <li>Will provide accurate and complete information during registration</li>
                            <li>Will maintain the security of your account credentials</li>
                            <li>Will comply with all applicable laws and regulations</li>
                        </ul>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">3. Account Registration</h2>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">3.1 Account Types</h3>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            UniScout offers two types of accounts:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2 mb-4">
                            <li><strong>Investor Accounts</strong> - For investors seeking to evaluate and discover startups</li>
                            <li><strong>Startup Accounts</strong> - For founders seeking funding and investor connections</li>
                        </ul>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">3.2 Account Responsibilities</h3>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            You are responsible for:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2">
                            <li>Maintaining the confidentiality of your account credentials</li>
                            <li>All activities that occur under your account</li>
                            <li>Notifying us immediately of any unauthorized access</li>
                            <li>Ensuring all information provided is accurate and up-to-date</li>
                        </ul>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">4. Use of Services</h2>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">4.1 Permitted Use</h3>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            You may use our Services to:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2 mb-4">
                            <li>Upload and analyze startup materials (if you are an investor)</li>
                            <li>Share your startup information with potential investors (if you are a founder)</li>
                            <li>Generate AI-powered investment analysis and reports</li>
                            <li>Connect with other users for legitimate business purposes</li>
                        </ul>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">4.2 Prohibited Use</h3>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            You agree NOT to:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2">
                            <li>Use the Services for any illegal or unauthorized purpose</li>
                            <li>Violate any laws in your jurisdiction</li>
                            <li>Infringe on intellectual property rights of others</li>
                            <li>Upload malicious code, viruses, or harmful content</li>
                            <li>Attempt to gain unauthorized access to our systems</li>
                            <li>Scrape, data mine, or use automated tools without permission</li>
                            <li>Harass, abuse, or harm other users</li>
                            <li>Share false, misleading, or fraudulent information</li>
                            <li>Resell or redistribute our Services without authorization</li>
                        </ul>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">5. Content and Intellectual Property</h2>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">5.1 Your Content</h3>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            You retain all ownership rights to the content you upload to UniScout ("User Content"). By uploading
                            User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, store, process,
                            and analyze your content to provide our Services.
                        </p>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            You represent and warrant that:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2 mb-4">
                            <li>You own or have the necessary rights to upload the User Content</li>
                            <li>Your User Content does not violate any third-party rights</li>
                            <li>Your User Content does not contain confidential information you are not authorized to share</li>
                        </ul>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">5.2 Our Content</h3>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            All content provided by UniScout, including but not limited to:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2 mb-4">
                            <li>AI-generated reports and analyses</li>
                            <li>Platform software and code</li>
                            <li>UniScout branding, logos, and trademarks</li>
                            <li>Design elements and user interface</li>
                        </ul>
                        <p className="text-slate-700 dark:text-slate-300">
                            These remain the exclusive property of UniScout and are protected by intellectual property laws.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">6. AI-Generated Content</h2>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            UniScout uses artificial intelligence to analyze startup information and generate investment reports.
                            You acknowledge and agree that:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2">
                            <li>AI-generated content is for informational purposes only</li>
                            <li>Reports should not be considered financial, legal, or investment advice</li>
                            <li>You should conduct your own due diligence before making investment decisions</li>
                            <li>AI analysis may contain errors or inaccuracies</li>
                            <li>We do not guarantee the accuracy or completeness of AI-generated reports</li>
                            <li>You use AI-generated content at your own risk</li>
                        </ul>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">7. Payments and Subscriptions</h2>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            If you subscribe to paid features:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2">
                            <li>You agree to pay all fees associated with your subscription</li>
                            <li>Fees are non-refundable except as required by law</li>
                            <li>We may change pricing with advance notice</li>
                            <li>You authorize us to charge your payment method on a recurring basis</li>
                            <li>You may cancel your subscription at any time</li>
                        </ul>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">8. Disclaimers</h2>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
                            EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2 mb-4">
                            <li>Warranties of merchantability or fitness for a particular purpose</li>
                            <li>Warranties that the Services will be uninterrupted or error-free</li>
                            <li>Warranties regarding the accuracy or reliability of information</li>
                            <li>Warranties that defects will be corrected</li>
                        </ul>
                        <p className="text-slate-700 dark:text-slate-300">
                            WE DO NOT ENDORSE, GUARANTEE, OR ASSUME RESPONSIBILITY FOR ANY STARTUP OR INVESTMENT
                            OPPORTUNITY PRESENTED THROUGH OUR PLATFORM.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">9. Limitation of Liability</h2>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            TO THE MAXIMUM EXTENT PERMITTED BY LAW, UNISCOUT SHALL NOT BE LIABLE FOR:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2">
                            <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                            <li>Loss of profits, revenue, data, or business opportunities</li>
                            <li>Investment losses or financial damages</li>
                            <li>Damages arising from your use or inability to use the Services</li>
                            <li>Damages arising from unauthorized access to your account</li>
                            <li>Damages arising from errors in AI-generated content</li>
                        </ul>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">10. Indemnification</h2>
                        <p className="text-slate-700 dark:text-slate-300">
                            You agree to indemnify, defend, and hold harmless UniScout, its officers, directors, employees,
                            and agents from any claims, damages, losses, liabilities, and expenses (including legal fees)
                            arising from:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2 mt-4">
                            <li>Your use of the Services</li>
                            <li>Your violation of these Terms</li>
                            <li>Your violation of any rights of another party</li>
                            <li>Your User Content</li>
                        </ul>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">11. Termination</h2>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            We reserve the right to suspend or terminate your account at any time, with or without notice, for:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2 mb-4">
                            <li>Violation of these Terms</li>
                            <li>Fraudulent or illegal activity</li>
                            <li>Provision of false or misleading information</li>
                            <li>Any conduct we deem harmful to the platform or other users</li>
                        </ul>
                        <p className="text-slate-700 dark:text-slate-300">
                            You may terminate your account at any time by contacting us. Upon termination, your right to
                            use the Services will immediately cease.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">12. Privacy</h2>
                        <p className="text-slate-700 dark:text-slate-300">
                            Your use of our Services is also governed by our{' '}
                            <Link to="/privacy-policy" className="text-primary hover:underline font-semibold">
                                Privacy Policy
                            </Link>
                            . Please review our Privacy Policy to understand how we collect, use, and protect your information.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">13. Dispute Resolution</h2>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">13.1 Governing Law</h3>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            These Terms shall be governed by and construed in accordance with the laws of the jurisdiction
                            where UniScout is incorporated, without regard to conflict of law principles.
                        </p>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">13.2 Arbitration</h3>
                        <p className="text-slate-700 dark:text-slate-300">
                            Any disputes arising from these Terms or your use of the Services shall be resolved through
                            binding arbitration, except where prohibited by law. You waive your right to a jury trial or
                            to participate in a class action lawsuit.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">14. General Provisions</h2>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">14.1 Entire Agreement</h3>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            These Terms constitute the entire agreement between you and UniScout regarding your use of the Services.
                        </p>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">14.2 Severability</h3>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions
                            will continue in full force and effect.
                        </p>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">14.3 Waiver</h3>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            Our failure to enforce any right or provision of these Terms will not be considered a waiver of
                            those rights.
                        </p>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">14.4 Assignment</h3>
                        <p className="text-slate-700 dark:text-slate-300">
                            You may not assign or transfer these Terms without our prior written consent. We may assign or
                            transfer these Terms at any time.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">15. Contact Information</h2>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            If you have any questions about these Terms of Service, please contact us:
                        </p>
                        <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 mt-4">
                            <p className="text-slate-700 dark:text-slate-300">
                                <strong>Email:</strong> legal@uniscout.ai
                            </p>
                            <p className="text-slate-700 dark:text-slate-300 mt-2">
                                <strong>Website:</strong> <a href="https://uniscout.ai" className="text-primary hover:underline">uniscout.ai</a>
                            </p>
                        </div>
                    </div>

                    {/* Back to Home Button */}
                    <div className="text-center mt-12">
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-logo-gradient text-white font-bold shadow-xl shadow-primary/25 hover:shadow-primary/30 transition-all transform hover:-translate-y-0.5"
                        >
                            <span className="material-icons">home</span>
                            Back to Home
                        </Link>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-background-light dark:bg-background-dark border-t border-slate-200 dark:border-slate-800 pt-12 pb-8 mt-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <img alt="uniscout Logo" className="h-10 mb-4 mx-auto" src="/logo.png" />
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            © 2026 UniScout. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
